<template>
  <v-dialog
    data-cy="upload-image-modal"
    :model-value="modelValue"
    :max-width="640"
    :fullscreen="smAndDown"
    :transition="smAndDown ? 'dialog-bottom-transition' : 'dialog-transition'"
    scrollable
    @update:model-value="closeDialog"
  >
    <v-card class="upload-modal">
      <v-card-title class="d-flex justify-space-between align-center pa-4 pb-2">
        <span class="modal-title">{{ titleText }}</span>
        <v-btn icon variant="text" data-cy="upload-image-close" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-4">
        <v-alert
          v-if="uploadError"
          type="warning"
          variant="tonal"
          border="start"
          colored-border
          class="mb-4"
          data-cy="upload-image-error"
        >
          {{ uploadError }}
        </v-alert>
        <div
          v-if="!sourceImageUrl"
          class="drop-zone"
          :class="{ 'drop-zone--active': isDragging }"
          data-cy="upload-image-dropzone"
          role="button"
          tabindex="0"
          @click="openFilePicker"
          @keydown.enter.prevent="openFilePicker"
          @keydown.space.prevent="openFilePicker"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
        >
          <v-icon size="48" color="primary">mdi-cloud-upload-outline</v-icon>
          <div class="drop-zone__title mt-3">{{ t('components.uploadImageModal.dropTitle') }}</div>
          <div class="drop-zone__subtitle">{{ t('components.uploadImageModal.dropSubtitle') }}</div>
          <div class="drop-zone__hint mt-2">{{ t('components.uploadImageModal.dropHint') }}</div>
          <input
            ref="fileInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="d-none"
            data-cy="upload-image-input"
            @change="onFileInput"
          />
        </div>

        <div v-else>
          <div class="text-caption text-medium-emphasis text-center mb-3">
            {{ t('components.uploadImageModal.cropHint') }}
          </div>
          <Cropper
            ref="cropperRef"
            class="cropper"
            data-cy="upload-image-crop"
            :src="sourceImageUrl"
            :stencil-props="{ aspectRatio: CERTIFICATE_PHOTO_ASPECT }"
            :default-position="savedCoordinates"
            :default-size="savedCoordinates"
            :min-width="100"
            :min-height="42"
            image-restriction="fit-area"
            @ready="imgReady = true"
          />
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="upload-modal__actions pa-4">
        <v-btn
          v-if="sourceImageUrl"
          variant="text"
          :disabled="uploading"
          data-cy="upload-image-replace"
          @click="resetSource"
        >
          {{ t('components.uploadImageModal.chooseAnother') }}
        </v-btn>
        <v-spacer />
        <v-btn variant="outlined" color="primary" :disabled="uploading" @click="closeDialog">
          {{ t('generic.labels.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!sourceImageUrl || uploading || !imgReady"
          :loading="uploading"
          data-cy="upload-image-confirm"
          @click="confirmUpload"
        >
          {{ t('components.uploadImageModal.confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useDisplay } from 'vuetify';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { postFile, ENDPOINTS } from '@/api/mainApi';
import store from '@/store';
import { useLabel } from '@/composables/useLabel';
import {
  CERTIFICATE_PHOTO_ASPECT,
  CERTIFICATE_PHOTO_OUTPUT_HEIGHT,
  CERTIFICATE_PHOTO_OUTPUT_WIDTH,
} from '@/utils/certificateImage';

type Coordinates = { left: number; top: number; width: number; height: number };

const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MIN_SOURCE_WIDTH = 800;
const MIN_SOURCE_HEIGHT = 430;

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'imageUploaded', payload: { url: string; dataUrl: string; imageKey: string }): void;
}>();

const { t } = useLabel();
const { smAndDown } = useDisplay();

const fileInput = ref<HTMLInputElement | null>(null);
const cropperRef = ref<InstanceType<typeof Cropper> | null>(null);

const sourceImageUrl = ref('');
const sourceObjectUrl = ref('');
const imgReady = ref(false);
const savedCoordinates = ref<Coordinates | null>(null);

const isDragging = ref(false);
const uploading = ref(false);
const uploadError = ref('');

const titleText = computed(() =>
  sourceImageUrl.value ? t('components.uploadImageModal.titleCrop') : t('components.uploadImageModal.titleUpload')
);

// ---- File handling ----
const openFilePicker = () => fileInput.value?.click();

const revokeSource = () => {
  if (!sourceObjectUrl.value) return;
  URL.revokeObjectURL(sourceObjectUrl.value);
  sourceObjectUrl.value = '';
};

const acceptFile = (file?: File | null) => {
  if (!file) return;
  uploadError.value = '';
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
    uploadError.value = t('components.uploadImageModal.invalidType');
    return;
  }
  if (file.size > MAX_FILE_BYTES) {
    uploadError.value = t('components.uploadImageModal.tooLarge');
    return;
  }
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    if (image.naturalWidth < MIN_SOURCE_WIDTH || image.naturalHeight < MIN_SOURCE_HEIGHT) {
      URL.revokeObjectURL(objectUrl);
      uploadError.value = t('components.uploadImageModal.tooSmall');
      return;
    }
    revokeSource();
    imgReady.value = false;
    savedCoordinates.value = null;
    sourceObjectUrl.value = objectUrl;
    sourceImageUrl.value = objectUrl;
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    uploadError.value = t('components.uploadImageModal.rejected');
  };
  image.src = objectUrl;
};

const onFileInput = (e: Event) => {
  const t = e.target as HTMLInputElement;
  acceptFile(t.files?.[0]);
  t.value = '';
};

const onDrop = (e: DragEvent) => {
  isDragging.value = false;
  acceptFile(e.dataTransfer?.files?.[0]);
};

// ---- Reset / close ----
const resetSource = () => {
  revokeSource();
  sourceImageUrl.value = '';
  imgReady.value = false;
  uploadError.value = '';
  savedCoordinates.value = null;
};

const closeDialog = () => emit('update:modelValue', false);

// ---- Render & upload ----
const renderOutput = (sourceCanvas: HTMLCanvasElement): Promise<{ blob: Blob; dataUrl: string }> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = CERTIFICATE_PHOTO_OUTPUT_WIDTH;
    canvas.height = CERTIFICATE_PHOTO_OUTPUT_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('Canvas unsupported'));
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(sourceCanvas, 0, 0, CERTIFICATE_PHOTO_OUTPUT_WIDTH, CERTIFICATE_PHOTO_OUTPUT_HEIGHT);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    canvas.toBlob(
      (blob) => (blob ? resolve({ blob, dataUrl }) : reject(new Error('Failed to encode image'))),
      'image/jpeg',
      0.92,
    );
  });

const confirmUpload = async () => {
  uploadError.value = '';
  uploading.value = true;
  try {
    const result = cropperRef.value?.getResult();
    if (!result?.canvas) throw new Error('Image not ready');
    const { blob, dataUrl } = await renderOutput(result.canvas);
    savedCoordinates.value = result.coordinates as Coordinates;

    const formData = new FormData();
    formData.append('file', blob, `certificate-${Date.now()}.jpg`);

    try {
      const response = await postFile(ENDPOINTS.CERTIFICATE_IMAGE, formData, undefined, false);
      const imageKey = response?.imageKey;
      if (!imageKey) {
        throw new Error('Upload succeeded without an image key');
      }
      emit('imageUploaded', { url: dataUrl, dataUrl, imageKey });
      closeDialog();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 413) {
        uploadError.value = t('components.uploadImageModal.tooLarge');
        uploading.value = false;
        return;
      }
      if (status === 429) {
        store.commit('setSnackbar', {
          active: true,
          color: 'warning',
          text: t('components.uploadImageModal.rateLimited'),
        });
        closeDialog();
        return;
      }
      if (status === 422) {
        uploadError.value = t('components.uploadImageModal.rejected');
        uploading.value = false;
        return;
      }
      window.log?.error?.('Image upload to backend failed.', err);
      uploadError.value = t('components.uploadImageModal.uploadFailedFallback');
      uploading.value = false;
      return;
    }
  } catch (err) {
    window.log?.error?.(err);
    uploadError.value = t('components.uploadImageModal.prepareError');
  } finally {
    uploading.value = false;
  }
};

watch(
  () => props.modelValue,
  (val, oldVal) => {
    if (oldVal && !val && !savedCoordinates.value) resetSource();
  },
);

onBeforeUnmount(revokeSource);
</script>

<style scoped>
.upload-modal {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.modal-title {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 18px;
}

/* ---- Drop zone ---- */
.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: #f9fafb;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.drop-zone:hover,
.drop-zone:focus-visible,
.drop-zone--active {
  border-color: var(--v-theme-primary, #1976d2);
  background: #eef2ff;
  outline: none;
}

.drop-zone__title {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #1f2a37;
}

.drop-zone__subtitle {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: #6b7280;
}

.drop-zone__hint {
  font-size: 12px;
  color: #9ca3af;
}

/* ---- Cropper ---- */
.cropper {
  height: min(60vh, 420px);
  background: #111827;
  border-radius: 8px;
  overflow: hidden;
}

/* ---- Actions ---- */
.upload-modal__actions {
  gap: 12px;
}

.upload-modal__actions .v-btn {
  border-radius: 8px;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
}

@media (max-width: 600px) {
  .cropper { height: 50vh; }
  .drop-zone { padding: 28px 12px; }
  .upload-modal__actions { flex-wrap: wrap; }
  .upload-modal__actions .v-btn { flex: 1 1 auto; }
}
</style>
